import { supabase, getAuthenticatedSupabase, withAuth } from '@/lib/supabase'
import type { 
  AuthUser, 
  EnhancedUserProfile, 
  UserRole, 
  Permission, 
  PermissionName, 
  RoleName,
  OrganizerProfile,
  UserRoleAssignment,
  AdminActivityLog
} from '@/lib/supabase'

// Request/Response interfaces
export interface LoginRequest {
  email: string
  password: string
  rememberMe?: boolean
}

export interface RegisterRequest {
  email: string
  password: string
  passwordConfirmation?: string
  displayName: string
  username: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  birthYear?: number
  birthMonth?: number
  birthDay?: number
  homeCity?: string
  stateRegion?: string
  country?: string
  gender?: string
  sexuality?: string
  relationshipStatus?: string
  agreeToTerms?: boolean
  agreeToPrivacy?: boolean
  profileData?: Partial<EnhancedUserProfile>
  // Additional fields for compatibility with AuthPage form
  age?: number
  accountType?: 'individual' | 'couple'
  location?: {
    city?: string
    state?: string
    country?: string
  }
}

// Enhanced registration request interface for comprehensive user registration
export interface EnhancedRegisterRequest {
  // Core authentication fields
  email: string
  password: string
  passwordConfirmation: string
  
  // Basic profile information
  username: string
  displayName: string
  dateOfBirth: string
  
  // Personal identity information
  gender: string | null
  sexuality: string[] | null
  relationshipStatus: string | null
  bodyType: string | null
  
  // Location data
  location: {
    city: string
    state: string
    country: string
    coordinates?: {
      lat: number
      lng: number
    }
  }
  
  // Account configuration
  accountType: 'individual' | 'couple'
  
  // Profile content
  bio?: string
  interests: string[]
  kinks: string[]
  limits: string[]
  lookingFor: string[]
  experienceLevel: string | null
  
  // Privacy and preferences
  profileVisibility: 'public' | 'members' | 'matches'
  ageRange: {
    min: number
    max: number
  }
  maxDistance: number
  
  // Legal and verification
  agreeToTerms: boolean
  agreeToPrivacy: boolean
  ageVerified: boolean
  marketingOptIn: boolean
  
  // Couple-specific fields (for couple accounts)
  partnerEmail?: string
  relationshipType?: string
  relationshipDuration?: string
  coupleName?: string
  
  // Optional metadata
  referralCode?: string
  inviteCode?: string
  ambassadorCode?: string
  
  // Client and tracking information
  clientInfo?: {
    userAgent: string
    ipAddress: string
    deviceType: 'mobile' | 'desktop' | 'tablet'
    referrer?: string
  }
  
  // Marketing attribution
  utm?: {
    source?: string
    medium?: string
    campaign?: string
    term?: string
    content?: string
  }
}

// Username availability check interfaces
export interface UsernameCheckRequest {
  username: string
}

export interface UsernameCheckResponse {
  available: boolean
  suggestions?: string[]
  reasons?: string[]
}

// Enhanced registration response with detailed feedback
export interface EnhancedRegistrationResponse {
  success: boolean
  user?: AuthUser
  profile?: EnhancedUserProfile
  session?: any
  verificationRequired?: boolean
  nextStep?: 'email_verification' | 'profile_completion' | 'age_verification' | 'couple_setup' | 'complete'
  errors?: Array<{
    field: string
    message: string
    code: string
    value?: any
  }>
  warnings?: Array<{
    field: string
    message: string
    suggestion?: string
  }>
  progress?: {
    currentStep: string
    completedSteps: string[]
    totalSteps: number
    percentComplete: number
  }
}

export interface AuthResponse {
  user: AuthUser
  profile: EnhancedUserProfile
  session: any
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  email: string
  password: string
  passwordConfirmation: string
}

export interface VerifyEmailRequest {
  token: string
  email: string
}

export interface OrganizerApplicationRequest {
  businessName?: string
  businessType?: string
  contactEmail: string
  contactPhone?: string
  websiteUrl?: string
  businessAddress?: string
  taxId?: string
  verificationDocuments?: string[]
  insuranceDocuments?: string[]
}

// Enhanced Authentication service with RBAC support
export const authService = {
  // ============================================================================
  // ENHANCED REGISTRATION METHODS
  // ============================================================================
  
  // Check username availability with suggestions
  checkUsernameAvailability: async (data: UsernameCheckRequest): Promise<UsernameCheckResponse> => {
    const { data: available, error } = await supabase
      .rpc('check_username_availability', { p_username: data.username })
    
    if (error) throw error
    
    if (available) {
      return { available: true }
    } else {
      // Get suggestions for alternative usernames
      const { data: suggestions } = await supabase
        .rpc('suggest_username_alternatives', { 
          p_username: data.username, 
          p_limit: 5 
        })
      
      return { 
        available: false, 
        suggestions: suggestions || [],
        reasons: ['Username is already taken']
      }
    }
  },

  // Enhanced registration with comprehensive validation and profile creation
  enhancedRegister: async (data: EnhancedRegisterRequest): Promise<EnhancedRegistrationResponse> => {
    try {
      // Step 1: Validate all required fields
      const errors: Array<{ field: string; message: string; code: string; value?: any }> = []
      const warnings: Array<{ field: string; message: string; suggestion?: string }> = []

      // Password confirmation validation
      if (data.password !== data.passwordConfirmation) {
        errors.push({
          field: 'passwordConfirmation',
          message: 'Passwords do not match',
          code: 'password_mismatch'
        })
      }

      // Age verification
      const birthDate = new Date(data.dateOfBirth)
      const age = Math.floor((Date.now() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
      if (age < 18) {
        errors.push({
          field: 'dateOfBirth',
          message: 'You must be at least 18 years old to register',
          code: 'age_requirement'
        })
      }

      // Couple account validation
      if (data.accountType === 'couple') {
        if (!data.partnerEmail || !data.relationshipType || !data.coupleName) {
          errors.push({
            field: 'partnerEmail',
            message: 'Partner email, relationship type, and couple name are required for couple accounts',
            code: 'couple_fields_required'
          })
        }
        
        if (data.partnerEmail === data.email) {
          errors.push({
            field: 'partnerEmail',
            message: 'Partner email cannot be the same as your email',
            code: 'self_partner_email'
          })
        }
      }

      // If there are validation errors, return early
      if (errors.length > 0) {
        return {
          success: false,
          errors,
          warnings,
          nextStep: 'basic_info'
        }
      }

      // Step 2: Check username availability
      const usernameCheck = await authService.checkUsernameAvailability({ username: data.username })
      if (!usernameCheck.available) {
        errors.push({
          field: 'username',
          message: `Username "${data.username}" is already taken`,
          code: 'username_taken',
          value: data.username
        })
        
        return {
          success: false,
          errors,
          warnings
        }
      }

      // Step 3: Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            username: data.username,
            display_name: data.displayName,
            account_type: data.accountType,
            age_verified: data.ageVerified,
            terms_accepted: data.agreeToTerms,
            privacy_policy_accepted: data.agreeToPrivacy
          }
        }
      })

      if (authError) {
        errors.push({
          field: 'email',
          message: authError.message,
          code: 'auth_error'
        })
        
        return {
          success: false,
          errors,
          warnings
        }
      }

      if (!authData.user) {
        throw new Error('Registration failed - no user data received')
      }

      // Step 4: Create comprehensive user profile
      const profileData = {
        user_id: authData.user.id,
        email: data.email,
        username: data.username,
        display_name: data.displayName,
        date_of_birth: data.dateOfBirth,
        gender: data.gender,
        sexuality: data.sexuality,
        relationship_status: data.relationshipStatus,
        body_type: data.bodyType,
        bio: data.bio,
        interests: data.interests,
        kinks: data.kinks,
        limits: data.limits,
        looking_for: data.lookingFor,
        experience_level: data.experienceLevel,
        city: data.location.city,
        state_region: data.location.state,
        country: data.location.country,
        coordinates: data.location.coordinates,
        account_type: data.accountType,
        profile_visibility: data.profileVisibility,
        age_range_min: data.ageRange.min,
        age_range_max: data.ageRange.max,
        max_distance: data.maxDistance,
        marketing_opt_in: data.marketingOptIn,
        referral_code: data.referralCode,
        invite_code: data.inviteCode,
        ambassador_code: data.ambassadorCode
      }

      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert(profileData)

      if (profileError) {
        // If profile creation fails, we should clean up the auth user
        // In practice, you might want to handle this more gracefully

        throw new Error('Registration failed - could not create user profile: ' + profileError.message)
      }

      // Step 5: Create age verification record
      const { error: verificationError } = await supabase
        .from('age_verifications')
        .insert({
          user_id: authData.user.id,
          verification_method: 'date_of_birth',
          verified_age: age,
          status: 'verified',
          verified_at: new Date().toISOString()
        })

      if (verificationError) {

        // Don't fail registration for this
      }

      // Step 6: Handle couple account setup
      if (data.accountType === 'couple' && data.partnerEmail && data.coupleName) {
        // Create couple profile (will be activated when partner accepts)
        const { error: coupleError } = await supabase
          .from('couples')
          .insert({
            primary_user_id: authData.user.id,
            couple_name: data.coupleName,
            relationship_type: data.relationshipType,
            relationship_duration: data.relationshipDuration,
            status: 'pending_partner_approval'
          })

        if (coupleError) {

          warnings.push({
            field: 'coupleName',
            message: 'Couple profile creation failed, but individual account was created successfully',
            suggestion: 'You can set up couple features later in your profile settings'
          })
        }

        // Send partner invitation (this would be implemented separately)
        // await sendPartnerInvitation(data.partnerEmail, authData.user.id, data.coupleName)
      }

      // Step 7: Process referral/invite codes
      if (data.referralCode || data.inviteCode) {
        try {
          if (data.referralCode) {
            await supabase.rpc('process_referral_code', {
              p_user_id: authData.user.id,
              p_referral_code: data.referralCode
            })
          }
          
          if (data.inviteCode) {
            await supabase.rpc('process_invite_code', {
              p_user_id: authData.user.id,
              p_invite_code: data.inviteCode
            })
          }
        } catch (error) {

          warnings.push({
            field: 'referralCode',
            message: 'Referral/invite code processing failed',
            suggestion: 'Contact support if you have issues with rewards'
          })
        }
      }

      // Step 8: Generate helpful warnings
      if (!data.bio || data.bio.trim().length === 0) {
        warnings.push({
          field: 'bio',
          message: 'Your profile would be more attractive with a bio',
          suggestion: 'Add a brief description about yourself and what you\'re looking for'
        })
      }

      if (data.interests.length < 3) {
        warnings.push({
          field: 'interests',
          message: 'Adding more interests helps you find better matches',
          suggestion: 'Consider adding at least 3-5 interests'
        })
      }

      // Step 9: Load enhanced profile
      const profile = await authService.getEnhancedProfile(authData.user.id)

      // Step 10: Determine next steps
      let nextStep: EnhancedRegistrationResponse['nextStep'] = 'complete'
      if (!authData.user.email_confirmed_at) {
        nextStep = 'email_verification'
      } else if (data.accountType === 'couple' && data.partnerEmail) {
        nextStep = 'couple_setup'
      }

      return {
        success: true,
        user: authData.user as AuthUser,
        profile,
        session: authData.session,
        verificationRequired: !authData.user.email_confirmed_at,
        nextStep,
        errors: [],
        warnings,
        progress: {
          currentStep: nextStep || 'complete',
          completedSteps: ['basic_info', 'personal_details', 'preferences', 'location'],
          totalSteps: data.accountType === 'couple' ? 8 : 7,
          percentComplete: nextStep === 'complete' ? 100 : 85
        }
      }

    } catch (error) {

      return {
        success: false,
        errors: [{
          field: 'form',
          message: error instanceof Error ? error.message : 'Registration failed with unknown error',
          code: 'registration_error'
        }],
        warnings: []
      }
    }
  },

  // ============================================================================
  // CORE AUTHENTICATION METHODS
  // ============================================================================
  
  // Login user with enhanced profile loading
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    console.log('🔑 Login attempt for:', data.email)
    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      })

      if (error) {
        console.error('❌ Login error:', error.message)
        // Log failed login attempt
        await authService.logLoginAttempt(data.email, false, error.message)
        throw error
      }
      
      if (!authData.user || !authData.session) {
        console.error('❌ No user/session data received')
        throw new Error('Login failed - no user data received')
      }

      console.log('✅ Login successful, user ID:', authData.user.id)
      // Log successful login
      await authService.logLoginAttempt(data.email, true)
      
      // Load enhanced user profile
      console.log('📋 Loading user profile...')
      const profile = await authService.getEnhancedProfile(authData.user.id)
      console.log('✅ Profile loaded successfully')

      return {
        user: authData.user as AuthUser,
        profile,
        session: authData.session
      }
    } catch (error) {
      console.error('❌ Login failed:', error)
      throw error
    }
  },
  
  // Register new user with comprehensive profile creation
  register: async (data: RegisterRequest): Promise<AuthResponse> => {

    try {
      // Handle both simplified form data and full RegisterRequest data
      
      // Convert age to birth components if provided
      let birthYear = data.birthYear
      let birthMonth = data.birthMonth
      let birthDay = data.birthDay
      let dateOfBirth = data.dateOfBirth
      
      if (data.age && !birthYear) {
        // Estimate birth year from age (rough approximation)
        birthYear = new Date().getFullYear() - data.age
        birthMonth = 1
        birthDay = 1
        dateOfBirth = `${birthYear}-01-01`

      }
      
      // Set default names if not provided
      const firstName = data.firstName || data.displayName?.split(' ')[0] || 'User'
      const lastName = data.lastName || data.displayName?.split(' ').slice(1).join(' ') || firstName
      
      // Validate age if provided
      if (data.age && data.age < 18) {
        throw new Error('You must be at least 18 years old to register')
      }
    
    // Skip username availability check for now since RPC functions are missing
    // Username uniqueness will be enforced by database constraints

    // Create auth user

    const { data: authData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          display_name: data.displayName,
          username: data.username,
          first_name: firstName,
          last_name: lastName,
          terms_accepted: data.agreeToTerms || true,
          privacy_policy_accepted: data.agreeToPrivacy || true,
          account_type: data.accountType || 'individual'
        }
      }
    })

    if (error) {

      throw error
    }
    if (!authData.user) {

      throw new Error('Registration failed - no user data received')
    }

    // Skip manual profile creation - let database triggers handle it or create later
    console.log('✅ Auth user created, skipping profile creation to avoid conflicts')
    
    // Create age verification record (optional - don't fail registration if this fails)
    try {
      const calculatedAge = data.age || (birthYear && birthMonth && birthDay 
        ? Math.floor((Date.now() - new Date(birthYear, birthMonth - 1, birthDay).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
        : 18)
      
      const { error: verificationError } = await supabase
        .from('age_verifications')
        .insert({
          user_id: authData.user.id,
          verification_method: 'date_of_birth',
          verified_age: calculatedAge,
          status: 'verified',
          verified_at: new Date().toISOString()
        })
      
      if (verificationError) {

      }
    } catch (error) {

    }
    
      // Load enhanced profile (will have default user role from trigger)

      const profile = await authService.getEnhancedProfile(authData.user.id)

      return {
        user: authData.user as AuthUser,
        profile,
        session: authData.session
      }
      
    } catch (error) {

      throw error
    }
  },
  
  // Sign out user
  signOut: async (): Promise<void> => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },
  
  // Forgot password
  forgotPassword: async (data: ForgotPasswordRequest): Promise<{ message: string }> => {
    const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    })
    
    if (error) throw error
    return { message: 'Password reset email sent' }
  },
  
  // Reset password
  resetPassword: async (data: ResetPasswordRequest): Promise<{ message: string }> => {
    if (data.password !== data.passwordConfirmation) {
      throw new Error('Passwords do not match')
    }
    
    const { error } = await supabase.auth.updateUser({
      password: data.password
    })
    
    if (error) throw error
    return { message: 'Password updated successfully' }
  },
  
  // Resend verification email
  resendVerification: async (email: string): Promise<{ message: string }> => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email
    })
    
    if (error) throw error
    return { message: 'Verification email sent' }
  },
  
  // Get current session
  getSession: async () => {
    return await supabase.auth.getSession()
  },
  
  // Refresh session
  refreshSession: async () => {
    return await supabase.auth.refreshSession()
  },
  
  // ============================================================================
  // PROFILE AND USER DATA METHODS
  // ============================================================================
  
  // Get enhanced user profile with roles and permissions
  getEnhancedProfile: async (userId: string): Promise<EnhancedUserProfile> => {

    if (!userId) {
      throw new Error('User ID is required to load profile')
    }
    
    try {
      // Get base profile from user_profiles table (the main profile table)
      let profile = null
      let profileError = null

      // Try user_profiles table first (this is the main profile table)
      const { data: profileByUserId, error: userIdError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)  // Changed from user_id to id - most tables use id as primary key
        .single()
      
      if (!userIdError && profileByUserId) {
        console.log('✅ Found profile in user_profiles table')
        profile = profileByUserId
      } else {
        console.log('⚠️ user_profiles query failed:', userIdError?.message)
        
        // Get auth user metadata to populate minimal profile
        let authUsername = `user_${userId.substring(0, 8)}`
        let authDisplayName = 'User'
        
        try {
          const { data: authUser, error: authError } = await supabase.auth.getUser()
          if (!authError && authUser?.user?.id === userId) {
            authUsername = authUser.user.user_metadata?.username || authUsername
            authDisplayName = authUser.user.user_metadata?.display_name || authUser.user.user_metadata?.username || authDisplayName
          }
        } catch (error) {
          console.log('⚠️ Could not fetch auth metadata for profile:', error)
        }
        
        // If profile doesn't exist, create a minimal one with auth metadata
        console.log('📝 Creating minimal profile for immediate auth functionality')
        profile = {
          id: userId,
          user_id: userId,
          display_name: authDisplayName,
          username: authUsername,
          date_of_birth: '2000-01-01',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        
        // Try to create the profile in the database
        try {
          const { data: createdProfile, error: createError } = await supabase
            .from('user_profiles')
            .insert({
              id: userId,
              display_name: authDisplayName,
              username: authUsername,
              date_of_birth: '2000-01-01',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single()
          
          if (!createError && createdProfile) {
            console.log('✅ Created profile in database with username:', authUsername)
            profile = createdProfile
          }
        } catch (error) {
          console.log('⚠️ Could not create profile in database:', error)
        }
      }

      // Load user roles and permissions from user_roles table

      let userRoles = []
      let permissionNames: string[] = []
      let effectiveRole = { role_name: 'user', level: 1 }
      
      try {
        const { data: roles, error: rolesError } = await supabase
          .from('user_roles')
          .select('*')
          .eq('user_id', userId)
          .eq('is_active', true)
      
      if (rolesError) {

      } else if (roles && roles.length > 0) {

        userRoles = roles
        
        // Extract permissions from roles
        const allPermissions = new Set<string>()
        let highestRole = { role_name: 'user', level: 1 }
        
        for (const role of roles) {

          // Add permissions from role.permissions field (database stored permissions)
          if (role.permissions && typeof role.permissions === 'object') {
            for (const [permission, enabled] of Object.entries(role.permissions)) {
              if (enabled === true) {
                allPermissions.add(permission)
              }
            }
          }
          
          // Determine role level and set effective role
          const roleLevel = role.role_name === 'super_admin' ? 4 : 
                           role.role_name === 'admin' ? 3 :
                           role.role_name === 'event_organizer' ? 2 : 1
          
          if (roleLevel > highestRole.level) {
            highestRole = { role_name: role.role_name, level: roleLevel }
          }
        }
        
        permissionNames = Array.from(allPermissions)
        effectiveRole = highestRole

      }
    } catch (error) {

    }
    
      // If no roles found, try to get from auth metadata as fallback
      if (userRoles.length === 0) {
        try {
          const { data: authUser, error: authError } = await supabase.auth.getUser()
          if (!authError && authUser?.user?.id === userId && authUser?.user?.user_metadata?.role) {
            const metaRole = authUser.user.user_metadata.role

            // Map metadata role to effective role
            if (metaRole === 'super_admin') {
              effectiveRole = { role_name: 'super_admin', level: 4 }
              permissionNames = [
                'manage_users', 'verify_users', 'manage_events', 'moderate_content', 
                'view_analytics', 'manage_organizers', 'access_admin_panel', 
                'create_events', 'manage_own_events', 'view_event_analytics', 'process_checkins'
              ]
            } else if (metaRole === 'admin') {
              effectiveRole = { role_name: 'admin', level: 3 }
              permissionNames = [
                'manage_users', 'verify_users', 'manage_events', 'moderate_content', 
                'view_analytics', 'manage_organizers', 'access_admin_panel'
              ]
            }
          }
        } catch (error) {

        }
      }
      
      // Load organizer profile if user is an organizer
      let organizerProfile = undefined
      try {
        if (effectiveRole.role_name === 'event_organizer' || effectiveRole.role_name === 'admin' || effectiveRole.role_name === 'super_admin') {
          const { data: orgProfiles, error: orgError } = await supabase
            .from('event_organizer_profiles')
            .select('*')
            .eq('user_id', userId)
          
          if (!orgError && orgProfiles && orgProfiles.length > 0) {
            organizerProfile = orgProfiles[0]

          }
        }
      } catch (error) {

      }
      
      const enhancedProfile = {
        ...profile,
        id: profile.id || userId,  // Ensure id is always present
        user_id: profile.user_id || userId,  // Ensure user_id is always present
        roles: userRoles,
        permissions: permissionNames,
        effectiveRole,
        organizerProfile
      }

      return enhancedProfile
    } catch (error) {

      throw error
    }
  },
  
  // Update user profile
  updateProfile: async (userId: string, updates: Partial<EnhancedUserProfile>): Promise<EnhancedUserProfile> => {
    // Remove RBAC-specific fields that shouldn't be updated directly
    const { roles, permissions, effectiveRole, organizerProfile, ...profileUpdates } = updates
    
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .update({
        ...profileUpdates,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)  // Fixed: use id consistently like in getEnhancedProfile
      .select()
      .single()
    
    if (error) throw error
    
    // Return enhanced profile
    return await authService.getEnhancedProfile(userId)
  },
  
  // Check if username is available
  checkUsername: async (username: string): Promise<{ available: boolean; suggestions?: string[] }> => {
    const { data: available, error } = await supabase
      .rpc('check_username_availability', { p_username: username })
    
    if (error) throw error
    
    if (available) {
      return { available: true }
    } else {
      // Get suggestions if username is taken
      const { data: suggestions } = await supabase
        .rpc('suggest_username_alternatives', { 
          p_username: username, 
          p_limit: 5 
        })
      
      return { 
        available: false, 
        suggestions: suggestions || [] 
      }
    }
  },
  
  // Check if email exists
  checkEmail: async (email: string): Promise<{ exists: boolean }> => {
    // Note: This is limited by Supabase's auth policies
    // In a real app, you'd need a server-side function for this
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: 'dummy-password-check'
      })
      
      // If error is about invalid credentials, email exists
      // If error is about email not found, email doesn't exist
      return { exists: error?.message !== 'Invalid login credentials' }
    } catch {
      return { exists: false }
    }
  },
  
  // ============================================================================
  // ROLE AND PERMISSION METHODS
  // ============================================================================
  
  // Check if user has specific permission
  hasPermission: async (userId: string, permission: PermissionName): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .rpc('user_has_permission', {
          target_user_id: userId,
          perm_name: permission
        })
      
      if (error) {

        return false
      }
      
      return data || false
    } catch (error) {

      return false
    }
  },
  
  // Get user's role level
  getUserRoleLevel: async (userId: string): Promise<number> => {
    try {
      const { data, error } = await supabase
        .rpc('get_user_role_level', { target_user_id: userId })
      
      if (error) {

        return 1 // Default to user level
      }
      
      return data || 1
    } catch (error) {

      return 1
    }
  },
  
  // Assign role to user (admin only)
  assignRole: async (userId: string, roleName: RoleName, assignedBy: string): Promise<void> => {
    // Get role ID
    const { data: role, error: roleError } = await supabase
      .from('user_roles')
      .select('id')
      .eq('role_name', roleName)
      .single()
    
    if (roleError || !role) throw new Error(`Role ${roleName} not found`)
    
    // Assign role
    const { error } = await supabase
      .from('user_role_assignments')
      .insert({
        user_id: userId,
        role_id: role.id,
        assigned_by: assignedBy,
        is_active: true
      })
    
    if (error) throw error
    
    // Log admin activity
    await authService.logAdminActivity(
      assignedBy,
      'role_assign',
      'user',
      userId,
      { role: roleName }
    )
  },
  
  // Remove role from user (admin only)
  removeRole: async (userId: string, roleName: RoleName, removedBy: string): Promise<void> => {
    // Get role ID
    const { data: role, error: roleError } = await supabase
      .from('user_roles')
      .select('id')
      .eq('role_name', roleName)
      .single()
    
    if (roleError || !role) throw new Error(`Role ${roleName} not found`)
    
    // Remove role assignment
    const { error } = await supabase
      .from('user_role_assignments')
      .update({ is_active: false })
      .eq('user_id', userId)
      .eq('role_id', role.id)
    
    if (error) throw error
    
    // Log admin activity
    await authService.logAdminActivity(
      removedBy,
      'role_remove',
      'user',
      userId,
      { role: roleName }
    )
  },
  
  // ============================================================================
  // EVENT ORGANIZER METHODS
  // ============================================================================
  
  // Apply to become event organizer
  applyForOrganizer: async (userId: string, applicationData: OrganizerApplicationRequest): Promise<void> => {
    // Update user profile organizer application status
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({
        organizer_application_status: 'pending',
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
    
    if (profileError) throw profileError
    
    // Create organizer profile
    const { error: orgError } = await supabase
      .from('event_organizer_profiles')
      .insert({
        user_id: userId,
        business_name: applicationData.businessName,
        business_type: applicationData.businessType,
        contact_email: applicationData.contactEmail,
        contact_phone: applicationData.contactPhone,
        website_url: applicationData.websiteUrl,
        business_address: applicationData.businessAddress,
        tax_id: applicationData.taxId,
        verification_documents: applicationData.verificationDocuments || [],
        insurance_documents: applicationData.insuranceDocuments || [],
        is_verified: false,
        verification_status: 'pending'
      })
    
    if (orgError) throw orgError
  },
  
  // Approve organizer application (admin only)
  approveOrganizer: async (userId: string, approvedBy: string): Promise<void> => {
    // Update organizer profile
    const { error: orgError } = await supabase
      .from('event_organizer_profiles')
      .update({
        is_verified: true,
        verification_status: 'approved',
        verified_at: new Date().toISOString(),
        verified_by: approvedBy
      })
      .eq('user_id', userId)
    
    if (orgError) throw orgError
    
    // Update user profile
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({
        organizer_application_status: 'approved',
        organizer_approved_at: new Date().toISOString(),
        organizer_approved_by: approvedBy
      })
      .eq('id', userId)
    
    if (profileError) throw profileError
    
    // Assign event_organizer role
    await authService.assignRole(userId, 'event_organizer', approvedBy)
  },
  
  // ============================================================================
  // ADMIN AND LOGGING METHODS
  // ============================================================================
  
  // Log admin activity
  logAdminActivity: async (
    adminUserId: string,
    actionType: string,
    targetType: string,
    targetId?: string,
    details?: Record<string, any>
  ): Promise<void> => {
    try {
      const { error } = await supabase
        .from('admin_activity_logs')
        .insert({
          id: crypto.randomUUID(),
          action_type: actionType,
          target_type: targetType,
          target_id: targetId,
          details: details || {},
          success: true
        })
      
      if (error) {

      }
    } catch (error) {

    }
  },
  
  // Ban user (admin only)
  banUser: async (
    userId: string,
    reason: string,
    bannedBy: string,
    expiresAt?: string
  ): Promise<void> => {
    const { error } = await supabase
      .from('user_profiles')
      .update({
        account_status: 'banned',
        suspension_reason: reason,
        suspension_expires_at: expiresAt,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
    
    if (error) throw error
    
    // Log admin activity
    await authService.logAdminActivity(
      bannedBy,
      'user_ban',
      'user',
      userId,
      { reason, expires_at: expiresAt }
    )
  },
  
  // Unban user (admin only)
  unbanUser: async (userId: string, unbannedBy: string): Promise<void> => {
    const { error } = await supabase
      .from('user_profiles')
      .update({
        account_status: 'active',
        suspension_reason: null,
        suspension_expires_at: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
    
    if (error) throw error
    
    // Log admin activity
    await authService.logAdminActivity(
      unbannedBy,
      'user_unban',
      'user',
      userId
    )
  },
  
  // ============================================================================
  // LOGIN ATTEMPT LOGGING
  // ============================================================================
  
  // Log login attempt (success or failure)
  logLoginAttempt: async (
    email: string, 
    success: boolean, 
    failureReason?: string,
    ipAddress?: string,
    userAgent?: string,
    userId?: string
  ): Promise<void> => {
    try {
      // Skip database logging for now since RPC functions are missing

      // In a real implementation, you'd log this to a table or external service
    } catch (error) {
      // Don't throw errors for logging - just log them

    }
  }
}

// Export individual methods for convenience
// ============================================================================
// VALIDATION AND UTILITY METHODS
// ============================================================================

// Validate registration data
export const validateRegistrationData = (data: RegisterRequest): { valid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(data.email)) {
    errors.push('Please enter a valid email address')
  }
  
  // Password validation
  if (data.password.length < 8) {
    errors.push('Password must be at least 8 characters long')
  }
  
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.password)) {
    errors.push('Password must contain at least one uppercase letter, one lowercase letter, and one number')
  }
  
  // Username validation
  if (data.username.length < 3 || data.username.length > 30) {
    errors.push('Username must be between 3 and 30 characters')
  }
  
  if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$/.test(data.username) && data.username.length !== 3) {
    errors.push('Username must start and end with a letter or number, and can only contain letters, numbers, underscores, and hyphens')
  }
  
  // Name validation
  if (!data.firstName || data.firstName.trim().length === 0) {
    errors.push('First name is required')
  }
  
  if (!data.lastName || data.lastName.trim().length === 0) {
    errors.push('Last name is required')
  }
  
  // Age validation
  if (!data.birthYear || !data.birthMonth || !data.birthDay) {
    errors.push('Complete birth date is required')
  } else {
    const birthDate = new Date(data.birthYear, data.birthMonth - 1, data.birthDay)
    const age = Math.floor((Date.now() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    
    if (age < 18) {
      errors.push('You must be at least 18 years old to register')
    }
    
    if (age > 120) {
      errors.push('Please enter a valid birth date')
    }
  }
  
  // Terms and privacy
  if (!data.agreeToTerms) {
    errors.push('You must agree to the Terms of Service')
  }
  
  if (!data.agreeToPrivacy) {
    errors.push('You must agree to the Privacy Policy')
  }
  
  return {
    valid: errors.length === 0,
    errors
  }
}

// Calculate age from birth date components
export const calculateAge = (birthYear: number, birthMonth: number, birthDay: number): number => {
  const birthDate = new Date(birthYear, birthMonth - 1, birthDay)
  const ageDifMs = Date.now() - birthDate.getTime()
  const ageDate = new Date(ageDifMs)
  return Math.abs(ageDate.getUTCFullYear() - 1970)
}

// Verify minimum age requirement
export const verifyMinimumAge = (birthYear: number, birthMonth: number, birthDay: number, minAge: number = 18): boolean => {
  const age = calculateAge(birthYear, birthMonth, birthDay)
  return age >= minAge
}

export const { 
  // Enhanced registration methods
  checkUsernameAvailability,
  enhancedRegister,
  
  // Core authentication methods
  login, 
  register,
  signOut,
  forgotPassword,
  resetPassword,
  resendVerification,
  getSession,
  refreshSession,
  checkUsername,
  checkEmail,
  getEnhancedProfile,
  updateProfile,
  hasPermission,
  getUserRoleLevel,
  assignRole,
  removeRole,
  applyForOrganizer,
  approveOrganizer,
  banUser,
  unbanUser,
  logAdminActivity,
  
  // Login attempt logging
  logLoginAttempt
} = authService

// Export supabase client for direct access if needed
export { supabase }